import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppAction} from '../../appActions';
import {CardNotesActionsEnum} from '../../data/cardNotes/cardNotesActions';
import {actionIsAccountDetailsPanelAction} from '../accounts/accountDetailsPanelActions';
import {accountDetailsSubStateReducer} from '../accounts/accountDetailsPanelReducer';
import {actionIsCreateAccountFormAction} from '../accounts/createAccountForm/createAccountFormActions';
import {contactDetailsCreateAccountFormReducer} from '../accounts/createAccountForm/createAccountFormReducer';
import {ContactDetailsPanelActionsEnum} from './contactDetailsPanelActions';
import {MergingStatusesEnum} from './contactDetailsPanelMergeState';
import {ContactDetailsPanelLocationsEnum, ContactDetailsPanelState} from './contactDetailsPanelState';
import {
  ContactDetailsPanelAccountDetailsSubState,
  ContactDetailsPanelCreateAccountSubState,
  ContactDetailsPanelMergeSubState,
  ContactDetailsPanelSubState,
  ContactDetailsSubStateTypesEnum,
  subStateIsAccountDetailsSubState,
  subStateIsCreateAccountSubState,
  subStateIsMergeSubState,
} from './contactDetailsPanelSubState';

export function contactDetailsPanelReducer(
  state: ContactDetailsPanelState,
  action: AppAction,
): ContactDetailsPanelState {
  return {
    ...contactDetailsReducer(state, action),
    subState: contactDetailsSubStateReducer(state.subState, action),
  };
}

function contactDetailsReducer(state: ContactDetailsPanelState, action: AppAction): ContactDetailsPanelState {
  switch (action.type) {
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        status: AsyncStatusesEnum.LOADING,
        selectedContactUid: action.payload.selectedContactUid,
        selectedCardId: action.payload.selectedCardId ?? state.selectedCardId,
        cancelTokenId: action.payload.cancelTokenId,
        selectedDomain: undefined,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_COMPLETED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        notesStatus: undefined,
        conversationStatus: undefined,
        noteIds: undefined,
        selectedCardId: action.payload.selectedCardId,
        cardIds: action.payload.cardIds,
        conversationIds: undefined,
        hasMoreConversations: undefined,
        selectedDomain: action.payload.selectedDomain,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_FAILED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        notesStatus: AsyncStatusesEnum.LOADING,
        noteIds: undefined,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_COMPLETED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        notesStatus: AsyncStatusesEnum.LOADED,
        noteIds: [...action.payload.noteIds],
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CARD_NOTES_FAILED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        notesStatus: AsyncStatusesEnum.FAILED,
      };
    }

    case ContactDetailsPanelActionsEnum.SCROLL_TO_SECTION: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        sectionToScrollTo: action.payload.section,
      };
    }
    case ContactDetailsPanelActionsEnum.SCROLLED_TO_SECTION: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        sectionToScrollTo: undefined,
      };
    }

    // Conversations
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS: {
      if (action.payload.panelId !== state.id) {
        return state;
      }
      return {
        ...state,
        conversationStatus: AsyncStatusesEnum.LOADING,
        conversationIds: undefined,
        hasMoreConversations: undefined,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_COMPLETED: {
      if (action.payload.panelId !== state.id) {
        return state;
      }
      return {
        ...state,
        conversationStatus: AsyncStatusesEnum.LOADED,
        conversationIds: [...action.payload.conversationIds],
        hasMoreConversations: action.payload.hasMoreConversations,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_CONVERSATIONS_FAILED: {
      if (action.payload.panelId !== state.id) {
        return state;
      }
      return {
        ...state,
        conversationStatus: AsyncStatusesEnum.FAILED,
      };
    }

    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_FAILED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
      };
    }
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_SUCCESS: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        cardIds: state.cardIds.filter((cardId) => !action.payload.mergedCardIds.includes(cardId)),
      };
    }

    case ContactDetailsPanelActionsEnum.UPDATE_SELECTED_CONTACT: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      // If we are in the contacts manager and already have a selected contact uid, we only need to update the contact uid.
      if (state.location === ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER && state.selectedContactUid) {
        return {
          ...state,
          selectedContactUid: action.payload.selectedContactUid,
        };
      }
      return {
        ...state,
        status: undefined,
        notesStatus: AsyncStatusesEnum.LOADING,
        noteIds: undefined,
        selectedContactUid: action.payload.selectedContactUid,
      };
    }

    // Contact creation failed go back to the loaded state
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CREATE_FAILED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
      };
    }

    case CardNotesActionsEnum.NOTE_DELETED:
      return {
        ...state,
        noteIds: (state.noteIds ?? []).filter((noteId) => noteId !== action.payload.note.id),
      };

    case CardNotesActionsEnum.NOTE_CREATED:
    case CardNotesActionsEnum.NOTE_UNDELETED:
      if (state.selectedCardId === action.payload.note.cardId) {
        return {
          ...state,
          noteIds: [...(state.noteIds ?? []), action.payload.note.id],
        };
      }

      return state;

    case ContactDetailsPanelActionsEnum.ACCOUNT_DOMAIN_VALIDATED: {
      if (state.id !== action.payload.panelId) {
        return state;
      }
      return {
        ...state,
        selectedDomain: action.payload.domain,
      };
    }

    default: {
      return state;
    }
  }
}

/*
 * SubState Reducers.
 */

function contactDetailsSubStateReducer(subState: ContactDetailsPanelSubState | undefined, action: AppAction) {
  // We are clearing any subState when this action occurs
  if (action.type === ContactDetailsPanelActionsEnum.OPEN_CONTACT_DETAILS) {
    return undefined;
  }

  const isOpenAccountDetailsAction = action.type === ContactDetailsPanelActionsEnum.OPEN_ACCOUNT_DETAILS;
  const isOpenMergePreviewAction =
    action.type === ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_STARTED;
  const isOpenCreateAccountFormAction =
    action.type === ContactDetailsPanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM;

  // Handle Account Details SubState
  if (
    !isOpenMergePreviewAction &&
    !isOpenCreateAccountFormAction &&
    ((subState && subStateIsAccountDetailsSubState(subState)) || isOpenAccountDetailsAction)
  ) {
    return contactDetailsAccountDetailsSubStateReducer(
      isOpenAccountDetailsAction ? undefined : (subState as ContactDetailsPanelAccountDetailsSubState),
      action,
    );
  }

  // Handle Merge SubState
  if (
    (!isOpenCreateAccountFormAction && subState && subStateIsMergeSubState(subState)) ||
    isOpenMergePreviewAction
  ) {
    return contactDetailsMergeSubStateReducer(
      isOpenMergePreviewAction ? undefined : (subState as ContactDetailsPanelMergeSubState),
      action,
    );
  }

  // Handle Create Account Form SubState
  if ((subState && subStateIsCreateAccountSubState(subState)) || isOpenCreateAccountFormAction) {
    return contactDetailsCreateAccountFormSubStateReducer(
      isOpenCreateAccountFormAction ? undefined : (subState as ContactDetailsPanelCreateAccountSubState),
      action,
    );
  }

  return subState;
}

function contactDetailsMergeSubStateReducer(
  subState: ContactDetailsPanelMergeSubState | undefined,
  action: AppAction,
): ContactDetailsPanelMergeSubState | undefined {
  switch (action.type) {
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_STARTED: {
      return {
        type: ContactDetailsSubStateTypesEnum.MERGE,
        mergeMainCardId: action.payload.mergeMainCardId,
        mergeCardIds: action.payload.mergeCardIds,
        mergeStatus: MergingStatusesEnum.MERGING,
        previewCardMerge: undefined,
      };
    }
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_PREVIEW_COMPLETED: {
      if (!subState) {
        return subState;
      }
      return {
        ...subState,
        previewCardMerge: action.payload.previewCardMerge,
      };
    }
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_FAILED: {
      if (!subState) {
        return subState;
      }
      return {
        ...subState,
        mergeStatus: MergingStatusesEnum.MERGE_FAILED,
      };
    }
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_MERGE_SUCCESS: {
      return undefined;
    }
    case ContactDetailsPanelActionsEnum.CONTACT_DETAILS_CLEAR_MERGE: {
      return undefined;
    }
    default:
      return subState;
  }
}

function contactDetailsAccountDetailsSubStateReducer(
  subState: ContactDetailsPanelAccountDetailsSubState | undefined,
  action: AppAction,
): ContactDetailsPanelAccountDetailsSubState | undefined {
  // If the action is a account details action, pass it to that reducer
  if (subState && actionIsAccountDetailsPanelAction(action)) {
    return accountDetailsSubStateReducer(subState, action);
  }
  switch (action.type) {
    case ContactDetailsPanelActionsEnum.OPEN_ACCOUNT_DETAILS: {
      return {
        type: ContactDetailsSubStateTypesEnum.ACCOUNT_DETAILS,
        accountId: action.payload.accountId,
        status: AsyncStatusesEnum.LOADING,
        cardsState: undefined,
        isFetchRequired: true,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA: {
      if (!subState) {
        return subState;
      }
      return {
        ...subState,
        status: AsyncStatusesEnum.LOADING,
        conversationStatus: undefined,
        conversationIds: undefined,
        hasMoreConversations: undefined,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA_COMPLETED: {
      if (!subState) {
        return subState;
      }
      // If the namespace is private, they do not have access to accounts
      if (action.payload.namespace && isTeammateNamespace(action.payload.namespace)) {
        return undefined;
      }
      return {
        ...subState,
        status: AsyncStatusesEnum.LOADING,
        accountId: action.payload.accountId,
        isFetchRequired: true,
      };
    }
    default:
      return subState;
  }
}

function contactDetailsCreateAccountFormSubStateReducer(
  subState: ContactDetailsPanelCreateAccountSubState | undefined,
  action: AppAction,
): ContactDetailsPanelCreateAccountSubState | undefined {
  // If the action is a create account form action, pass it to that reducer
  if (subState && actionIsCreateAccountFormAction(action)) {
    return contactDetailsCreateAccountFormReducer(subState, action);
  }
  switch (action.type) {
    case ContactDetailsPanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM: {
      return {
        type: ContactDetailsSubStateTypesEnum.CREATE_ACCOUNT,
        name: '',
        description: '',
        avatarFile: undefined,
        domains: [],
        customAttributes: action.payload.customAttributes,
        createStatus: undefined,
        source: action.payload.source,
        cardId: action.payload.cardId,
      };
    }
    case ContactDetailsPanelActionsEnum.FETCH_CONTACT_DETAILS_DATA:
      return undefined;
    default:
      return subState;
  }
}
